// Reports: deliveries by day and route, missed and late stops, proof of delivery lookup,
// driver activity, and planned vs actual route time. Data is read once per date range.
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase.js';
import { S, emit, driverName } from '../store.js';
import { esc, ymd, fmtTime, statusAt, dowOf, minuteOfDay, hoursText, intervals, haversineMiles, prettyDate } from '../util.js';

export const R = { from: null, to: null, view: 'deliveries', loading: false, loadedKey: '', stops: [], routes: [], tracks: [], error: '', pod: { q: '', results: null, loading: false } };

const addDays = (d, n) => ymd(new Date(`${d}T12:00:00Z`).getTime() + n * 86400000);
const routeOfStop = (s) => s.route || S.offices[s.officeId]?.route || 'No route';
const officeNm = (s) => S.offices[s.officeId]?.name || s.officeName || 'Unknown office';
const minutes = (ms) => (ms == null ? '' : `${Math.round(ms / 60000)} min`);
const hrsMin = (ms) => { if (ms == null || ms < 0) return ''; const m = Math.round(ms / 60000); return m >= 60 ? `${Math.floor(m / 60)} hr ${m % 60} min` : `${m} min`; };
const md = (d) => prettyDate(d, { weekday: 'short', month: 'numeric', day: 'numeric' });

export async function loadReports(force = false) {
  if (!R.to) { R.to = ymd(); R.from = addDays(R.to, -6); }
  const key = `${R.from}|${R.to}`;
  if (!force && (R.loadedKey === key || R.loading)) return;
  R.loading = true; R.error = ''; emit();
  try {
    const rng = (c) => query(collection(db, c), where('date', '>=', R.from), where('date', '<=', R.to));
    const [st, ro, tr] = await Promise.all([getDocs(rng('stops')), getDocs(rng('routes')), getDocs(rng('tracks'))]);
    R.stops = st.docs.map((d) => ({ id: d.id, ...d.data() }));
    R.routes = ro.docs.map((d) => ({ id: d.id, ...d.data() }));
    R.tracks = tr.docs.map((d) => ({ id: d.id, ...d.data() }));
    R.loadedKey = key;
  } catch (e) { R.error = e.message || 'Could not load the reports.'; }
  R.loading = false; emit();
}

export async function lookupCase(q) {
  R.pod = { q, results: null, loading: true }; emit();
  try {
    const snap = await getDocs(query(collection(db, 'stops'), where('cases', 'array-contains', q.trim())));
    R.pod = { q, results: snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => b.date.localeCompare(a.date)), loading: false };
  } catch (e) { R.pod = { q, results: [], loading: false, error: e.message }; }
  emit();
}

// Was the office open when the stop was logged? Uses the office's current saved hours.
function arrivedClosed(s) {
  const o = S.offices[s.officeId];
  if (s.status !== 'delivered' || !s.deliveredAt || !o?.hours) return null;
  const st = statusAt(o.hours, dowOf(s.date), minuteOfDay(s.deliveredAt));
  return st.s === 'closed' ? `${st.label} (hours ${hoursText(intervals(o.hours, dowOf(s.date)))})` : null;
}
const lateBy = (s) => (s.status === 'delivered' && s.plannedEta && s.deliveredAt ? s.deliveredAt - s.plannedEta : null);

// ----- report builders: each returns { cols, rows } (rows are arrays of display strings) -----
function deliveries() {
  const by = new Map();
  for (const s of R.stops) {
    const k = `${s.date}|${routeOfStop(s)}`;
    if (!by.has(k)) by.set(k, { date: s.date, route: routeOfStop(s), drivers: new Set(), stops: 0, cases: 0, tryins: 0, pickups: 0, done: 0, missed: 0, closed: 0, signed: 0 });
    const g = by.get(k);
    g.stops++; g.cases += (s.cases || []).length - (s.tryins || []).length; g.tryins += (s.tryins || []).length; g.pickups += (s.pickups || []).length;
    if (s.driverEmail) g.drivers.add(driverName(s.driverEmail));
    if (s.status === 'delivered') g.done++;
    if (s.status === 'skipped') g.missed++;
    if (arrivedClosed(s)) g.closed++;
    if (s.signaturePath) g.signed++;
  }
  const list = [...by.values()].sort((a, b) => b.date.localeCompare(a.date) || a.route.localeCompare(b.route));
  const tot = list.reduce((t, g) => { for (const k of ['stops', 'cases', 'tryins', 'pickups', 'done', 'missed', 'closed', 'signed']) t[k] += g[k]; return t; }, { stops: 0, cases: 0, tryins: 0, pickups: 0, done: 0, missed: 0, closed: 0, signed: 0 });
  const row = (g, label, route, drv) => [label, route, drv, g.stops, g.cases, g.tryins, g.pickups, g.done, g.missed, g.closed, g.signed, g.stops ? `${Math.round((g.done / g.stops) * 100)}%` : ''];
  return {
    cols: ['Date', 'Route', 'Driver', 'Stops', 'Cases', 'Try-ins', 'Pickups', 'Done', 'Missed', 'Arrived closed', 'Signed', 'Completed'],
    rows: list.map((g) => row(g, md(g.date), g.route, [...g.drivers].join(', '))),
    total: list.length > 1 ? row(tot, 'Total', '', '') : null,
  };
}

function missed() {
  const out = [];
  for (const s of R.stops) {
    const closed = arrivedClosed(s); const late = lateBy(s);
    if (s.status === 'skipped') out.push([s, 'Not delivered', s.note || '']);
    else if (closed) out.push([s, 'Arrived while closed', closed]);
    else if (late != null && late > 30 * 60000) out.push([s, 'Late', `${minutes(late)} after the planned ${fmtTime(s.plannedEta)}`]);
  }
  out.sort((a, b) => b[0].date.localeCompare(a[0].date) || (a[0].deliveredAt || 0) - (b[0].deliveredAt || 0));
  return {
    cols: ['Date', 'Time', 'Office', 'Route', 'Driver', 'What happened', 'Details', 'Cases'],
    rows: out.map(([s, what, why]) => [md(s.date), s.deliveredAt ? fmtTime(s.deliveredAt) : '', officeNm(s), routeOfStop(s), driverName(s.driverEmail), what, why, (s.cases || []).join(', ')]),
  };
}

function trackMiles(email, date) {
  const t = R.tracks.find((x) => x.driverEmail === email && x.date === date);
  const pts = (t?.points || []).slice().sort((a, b) => a.ts - b.ts);
  let mi = 0; for (let i = 1; i < pts.length; i++) mi += haversineMiles(pts[i - 1], pts[i]);
  return pts.length > 1 ? mi : null;
}

// One row per driver per day they had stops.
function driverDays() {
  const by = new Map();
  for (const s of R.stops) {
    if (!s.driverEmail) continue;
    const k = `${s.date}|${s.driverEmail}`;
    if (!by.has(k)) by.set(k, { date: s.date, email: s.driverEmail, stops: [] });
    by.get(k).stops.push(s);
  }
  return [...by.values()].map((d) => {
    const r = R.routes.find((x) => x.date === d.date && x.driverEmail === d.email) || {};
    const logged = d.stops.filter((s) => s.status !== 'pending' && s.deliveredAt).map((s) => s.deliveredAt).sort((a, b) => a - b);
    const start = r.startedAt || null;
    const end = r.finishedAt || logged[logged.length - 1] || null;
    return { ...d, r, start, end, done: d.stops.filter((s) => s.status === 'delivered').length, missed: d.stops.filter((s) => s.status === 'skipped').length, miles: trackMiles(d.email, d.date) };
  }).sort((a, b) => b.date.localeCompare(a.date) || driverName(a.email).localeCompare(driverName(b.email)));
}

function activity() {
  const list = driverDays();
  return {
    cols: ['Date', 'Driver', 'Started', 'Finished', 'Time on the road', 'Stops', 'Done', 'Missed', 'Miles (tracked)', 'Avg per stop'],
    rows: list.map((d) => {
      const span = d.start && d.end ? d.end - d.start : null;
      return [md(d.date), driverName(d.email), d.start ? fmtTime(d.start) : 'Not started', d.r.finishedAt ? fmtTime(d.r.finishedAt) : d.end ? `${fmtTime(d.end)} (last stop)` : '',
        hrsMin(span), d.stops.length, d.done, d.missed, d.miles != null ? d.miles.toFixed(1) : '', span && d.done ? minutes(span / d.done) : ''];
    }),
  };
}

function plannedVsActual() {
  const list = driverDays();
  return {
    cols: ['Date', 'Driver', 'Planned', 'Actual', 'Difference', 'Planned by', 'Stops on time', 'Avg vs planned ETA'],
    rows: list.map((d) => {
      const planned = d.r.plannedSeconds ? d.r.plannedSeconds * 1000 : null;
      const actual = d.start && d.end ? d.end - d.start : null;
      const diff = planned != null && actual != null ? actual - planned : null;
      const deltas = d.stops.map(lateBy).filter((x) => x != null);
      const onTime = deltas.filter((x) => x <= 15 * 60000).length;
      const avg = deltas.length ? deltas.reduce((a, b) => a + b, 0) / deltas.length : null;
      const sign = (ms) => (ms == null ? '' : `${ms >= 0 ? '+' : '-'}${hrsMin(Math.abs(ms))}`);
      return [md(d.date), driverName(d.email), hrsMin(planned) || (d.r.optimizer === 'estimate' ? 'Estimate only' : 'Not optimized'), hrsMin(actual),
        sign(diff), d.r.optimizer === 'google' ? 'Google' : d.r.optimizer === 'estimate' ? 'Estimate' : '', deltas.length ? `${onTime} of ${deltas.length}` : '', sign(avg)];
    }),
    note: 'Planned is Google\'s drive and stop time from the last optimize. Actual runs from Start route to End route (or the last logged stop). A stop counts as on time within 15 minutes of its planned ETA.',
  };
}

const VIEWS = [
  ['deliveries', 'Deliveries by day and route', deliveries],
  ['missed', 'Missed and late stops', missed],
  ['pod', 'Proof of delivery', null],
  ['activity', 'Driver activity', activity],
  ['planned', 'Planned vs actual', plannedVsActual],
];

export function currentTable() {
  const v = VIEWS.find(([k]) => k === R.view);
  return v?.[2] ? v[2]() : null;
}

function table(t) {
  if (!t.rows.length) return '<div class="empty">Nothing in this date range.</div>';
  return `<div class="tbl-wrap"><table><thead><tr>${t.cols.map((c) => `<th>${esc(c)}</th>`).join('')}</tr></thead><tbody>
    ${t.rows.map((r) => `<tr>${r.map((c) => `<td>${esc(c)}</td>`).join('')}</tr>`).join('')}
    ${t.total ? `<tr class="tot">${t.total.map((c) => `<td><b>${esc(c)}</b></td>`).join('')}</tr>` : ''}</tbody></table></div>
    ${t.note ? `<div class="pad small muted">${esc(t.note)}</div>` : ''}`;
}

function podView() {
  const p = R.pod;
  const res = p.results;
  return `<div class="pad stack"><div class="row"><input id="podQ" placeholder="Case number, like 26100437" value="${esc(p.q)}" inputmode="numeric" autocomplete="off" style="max-width:260px"><button class="btn primary" data-act="podSearch">Look up</button></div>
    ${p.loading ? '<div class="muted">Looking…</div>' : res == null ? '<div class="small muted">Find who delivered a case, when, who received it, and the signature and photo. Searches every day, not just the dates above.</div>'
    : !res.length ? `<div class="empty">No stop found with case ${esc(p.q)}.</div>`
    : res.map((s) => `<div class="drv"><div class="spread"><b>${esc(officeNm(s))}</b><span class="small muted">${esc(prettyDate(s.date))}</span></div>
        <div class="small">${s.status === 'delivered' ? `Delivered ${esc(fmtTime(s.deliveredAt))} by ${esc(driverName(s.driverEmail))}${s.receivedBy ? `, received by ${esc(s.receivedBy)}` : ''}` : s.status === 'skipped' ? `Not delivered${s.note ? `: ${esc(s.note)}` : ''} (${esc(driverName(s.driverEmail))})` : `Still open, ${s.driverEmail ? `with ${esc(driverName(s.driverEmail))}` : 'no driver yet'}`}</div>
        ${s.emailedAt ? `<div class="small muted">Office emailed ${esc(fmtTime(s.emailedAt))}</div>` : ''}
        <div class="row">${s.signaturePath ? `<button class="btn small" data-act="sig" data-id="${esc(s.id)}" data-src="pod">View signature</button>` : '<span class="small muted">No signature</span>'}${s.photoPath ? `<button class="btn small" data-act="photo" data-id="${esc(s.id)}" data-src="pod">View photo</button>` : ''}</div></div>`).join('')}</div>`;
}

export function reportsTab() {
  if (!R.to) { R.to = ymd(); R.from = addDays(R.to, -6); }
  const t = R.view === 'pod' ? null : currentTable();
  return `<section class="panel"><div class="pad stack">
    <div class="spread"><h2>Reports</h2>
      <div class="row"><label class="row small nowrap">From<input type="date" id="repFrom" value="${esc(R.from)}"></label><label class="row small nowrap">To<input type="date" id="repTo" value="${esc(R.to)}"></label>
        ${R.view !== 'pod' ? `<button class="btn ghost small" data-act="repCsv" ${t && t.rows.length ? '' : 'disabled'}>Download CSV</button>` : ''}</div></div>
    <nav class="tabs subtabs" role="tablist">${VIEWS.map(([k, l]) => `<button role="tab" aria-selected="${R.view === k}" data-act="repView" data-view-key="${k}">${l}</button>`).join('')}</nav></div>
    ${R.error ? `<div class="pad"><div class="note warn">${esc(R.error)}</div></div>` : ''}
    ${R.view === 'pod' ? podView() : R.loading && !R.loadedKey ? '<div class="empty">Loading…</div>' : table(t)}
  </section>`;
}

export function csvFor() {
  const t = currentTable(); if (!t) return null;
  const cell = (v) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
  const lines = [t.cols, ...t.rows, ...(t.total ? [t.total] : [])].map((r) => r.map(cell).join(','));
  const name = `${(VIEWS.find(([k]) => k === R.view) || [])[1] || 'report'} ${R.from} to ${R.to}.csv`;
  return { name, text: lines.join('\r\n') };
}
